import { ActionButtonApi, MessageSuggestionsDto, MessageType } from '../client/types';
import { Board, Message, MessageFrom, MessageMethod } from '../types';
import { TrackEventWithoutPageview } from './tracker';

export interface NewMessageEventDetail {
  message: string;
  type: MessageType;
  from: MessageFrom;
  method: MessageMethod;
  suggestions?: MessageSuggestionsDto[];
}

export interface OpenNewLinkEventDetail {
  href: string;
  newTab: boolean;
}

export interface ActionEventDetail {
  action: ActionButtonApi;
}

export interface UpdateMessageEventDetail {
  messageId: string;
  message: Partial<Message>;
}

export interface LoginEventDetail {
  messageIdTrigger?: string;
}

/**
 * Dispatch an event to track an event
 * @param tracking
 */
export const triggerTrackEvent = (tracking: TrackEventWithoutPageview) => {
  window.document.dispatchEvent(new CustomEvent<TrackEventWithoutPageview>('sofia_trackingEvent', { detail: tracking }));
};

/**
 * Dispatch an event to open a new link
 * @param link
 */
export const triggerOpenNewLink = (link: OpenNewLinkEventDetail) => {
  window.document.dispatchEvent(new CustomEvent<OpenNewLinkEventDetail>('sofia_openNewLink', { detail: link }));
};

/**
 * Dispatch an event to trigger a submit chat message
 * @param messageEventDetail
 */
export const triggerSubmitChatMessage = (messageEventDetail: NewMessageEventDetail) => {
  window.document.dispatchEvent(
    new CustomEvent<NewMessageEventDetail>('sofia_submitChatMessage', {
      detail: messageEventDetail,
    }),
  );
};

/**
 * Dispatch an event to trigger a submit action
 * @param action
 */
export const triggerSubmitAction = ({ action }: ActionEventDetail) => {
  window.document.dispatchEvent(
    new CustomEvent<ActionEventDetail>('sofia_submitAction', {
      detail: {
        action,
      },
    }),
  );
};

/**
 * Dispatch an event to update a message
 * @param message
 * @param messageId
 */
export const triggerUpdateMessage = ({ message, messageId }: UpdateMessageEventDetail) => {
  window.document.dispatchEvent(
    new CustomEvent<UpdateMessageEventDetail>('sofia_updateMessage', {
      detail: {
        messageId,
        message,
      },
    }),
  );
};

/**
 * Dispatch an event to trigger a login event
 */
export const triggerLoginEvent = (loginEvent: LoginEventDetail) => {
  window.document.dispatchEvent(
    new CustomEvent<LoginEventDetail>('sofia_login', {
      detail: loginEvent,
    }),
  );
};

/**
 * Register a listener for receiving a new board event
 * @param callback
 */
export const onReceiveNewBoard = (callback: (board: Board) => void) => {
  window.document.addEventListener('sofia_receiveNewBoard', event => {
    const newBoard = (event as CustomEvent<Board | undefined>).detail;

    if (!!newBoard) {
      console.debug('Execute callback because received new board event: ', event);
      callback(newBoard);
    }
  });
};

/**
 * Dispatch an event to trigger a Hotjar event
 */
export function triggerHotJarEvent(eventName: string) {
  const sessionKey = `${eventName}_hj`;
  if (typeof window.hj === 'function' && typeof sessionStorage?.getItem == 'function' && sessionStorage.getItem(sessionKey) === null) {
    sessionStorage.setItem(sessionKey, 'true');
    window.hj('event', eventName);
    console.debug('Fired hj: ', eventName);
  } else {
    console.debug("Can't fire hj: ", eventName);
  }
}
